/**
 * Offline Storage Utility
 * Uses IndexedDB to store pending entries for tasks and attendance
 * that can be synced when the device comes back online.
 */

const DB_NAME = 'forge_offline';
const DB_VERSION = 3;

// Store names
export const STORES = {
  PENDING_ATTENDANCE: 'pending_attendance',
  PENDING_TASKS: 'pending_tasks',
  PENDING_ASSIGNMENT_UPDATES: 'pending_assignment_updates',
  SYNC_QUEUE: 'sync_queue',
  OFFLINE_CACHE: 'offline_cache',
} as const;

export type StoreType = typeof STORES[keyof typeof STORES];

export interface PendingAttendance {
  id: string;
  organization_id: string;
  event_id: string | null;
  person_id: string;
  event_date: string;
  event_name: string;
  check_in_method: string;
  check_in_time: string;
  notes?: string | null;
  pickup_code?: string | null;
  created_offline: string;
  is_first_visit?: boolean | null;
  synced?: boolean;
  // Discriminator: true for kids-check-in kiosk rows (queued by useOfflineCheckIn).
  // The shared pending-attendance store is also fed by general attendance
  // (Attendance.tsx) and mobile attendance. The offline flush engines route ONLY
  // kiosk rows through the volunteer-authorizing record_kids_check_in RPC; general
  // attendance stays on the RLS-governed direct insert (volunteers are granted
  // childcare:add but NOT attendance:add). Absent on pre-existing queued rows, so
  // the flush also treats check_in_method === 'kiosk' as a kids row (belt-and-suspenders).
  kiosk_check_in?: boolean;
  // The kiosk evaluates the practical-minor + child-event context before it
  // creates this row. Sync uses the persisted decision so an offline replay
  // cannot re-submit a code for a general event.
  pickup_code_context?: boolean;
  // The room selected on the kiosk at check-in time (#4684). Threaded through
  // to record_kids_check_in so its room-capacity guard can actually run —
  // without it the RPC has no room to enforce capacity against. Absent on
  // rows queued before this field existed; the flush treats a missing value
  // the same as "no room selected" (guard no-ops, matching prior behavior).
  room_id?: string | null;
}

export interface PendingTask {
  id: string;
  organization_id: string;
  title: string;
  description?: string;
  priority: 'low' | 'medium' | 'high';
  due_date?: string;
  project_id?: string;
  request_id?: string;
  created_offline: string;
  synced?: boolean;
}

export interface PendingAssignmentUpdate {
  id: string;
  assignment_id: string;
  organization_id: string;
  service_id: string;
  status: string;
  confirmed_at?: string | null;
  created_offline: string;
  synced?: boolean;
}

export interface SyncQueueItem {
  id: string;
  store: StoreType;
  item_id: string;
  operation: 'create' | 'update' | 'delete';
  timestamp: string;
  retries: number;
}

export type OfflineReadScope =
  | 'attendance_people'
  | 'attendance_upcoming_events'
  | 'attendance_upcoming_services'
  | 'attendance_roster_events'
  | 'volunteer_assignments_upcoming'
  | 'volunteer_assignments_past';

export const OFFLINE_READ_SCOPES = {
  CHECK_IN: [
    'attendance_people',
    'attendance_upcoming_events',
    'attendance_upcoming_services',
    'attendance_roster_events',
  ] as OfflineReadScope[],
  VOLUNTEER_ASSIGNMENTS: {
    UPCOMING: 'volunteer_assignments_upcoming',
    PAST: 'volunteer_assignments_past',
  },
} as const;

export interface OfflineCacheEntry<T = unknown> {
  id: string;
  organization_id: string;
  scope: OfflineReadScope;
  payload: T;
  updated_at: string;
}

let db: IDBDatabase | null = null;

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;

      // Create object stores
      if (!database.objectStoreNames.contains(STORES.PENDING_ATTENDANCE)) {
        const attendanceStore = database.createObjectStore(STORES.PENDING_ATTENDANCE, { keyPath: 'id' });
        attendanceStore.createIndex('organization_id', 'organization_id', { unique: false });
        attendanceStore.createIndex('synced', 'synced', { unique: false });
      }

      if (!database.objectStoreNames.contains(STORES.PENDING_TASKS)) {
        const tasksStore = database.createObjectStore(STORES.PENDING_TASKS, { keyPath: 'id' });
        tasksStore.createIndex('organization_id', 'organization_id', { unique: false });
        tasksStore.createIndex('synced', 'synced', { unique: false });
      }

      if (!database.objectStoreNames.contains(STORES.PENDING_ASSIGNMENT_UPDATES)) {
        const assignmentsStore = database.createObjectStore(STORES.PENDING_ASSIGNMENT_UPDATES, { keyPath: 'id' });
        assignmentsStore.createIndex('organization_id', 'organization_id', { unique: false });
        assignmentsStore.createIndex('assignment_id', 'assignment_id', { unique: false });
        assignmentsStore.createIndex('synced', 'synced', { unique: false });
      }

      if (!database.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncStore = database.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id' });
        syncStore.createIndex('store', 'store', { unique: false });
        syncStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      if (!database.objectStoreNames.contains(STORES.OFFLINE_CACHE)) {
        const cacheStore = database.createObjectStore(STORES.OFFLINE_CACHE, { keyPath: 'id' });
        cacheStore.createIndex('organization_id', 'organization_id', { unique: false });
        cacheStore.createIndex('scope', 'scope', { unique: false });
        cacheStore.createIndex('updated_at', 'updated_at', { unique: false });
      }
    };
  });
}

// Generic store operations
export async function addToStore<T extends { id: string }>(
  storeName: StoreType,
  item: T,
  options?: { operation?: SyncQueueItem['operation'] }
): Promise<void> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([storeName, STORES.SYNC_QUEUE], 'readwrite');
    const store = transaction.objectStore(storeName);
    const syncStore = transaction.objectStore(STORES.SYNC_QUEUE);

    store.put(item);

    // Add to sync queue
    const syncItem: SyncQueueItem = {
      id: crypto.randomUUID(),
      store: storeName,
      item_id: item.id,
      operation: options?.operation ?? 'create',
      timestamp: new Date().toISOString(),
      retries: 0,
    };
    syncStore.put(syncItem);

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function getFromStore<T>(storeName: StoreType, id: string): Promise<T | undefined> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getAllFromStore<T>(storeName: StoreType): Promise<T[]> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getUnsyncedItems<T>(storeName: StoreType): Promise<T[]> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = () => {
      const items = request.result as Array<T & { synced?: boolean }>;
      resolve(items.filter(item => !item.synced) as T[]);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function markAsSynced(storeName: StoreType, id: string): Promise<void> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.get(id);

    request.onsuccess = () => {
      const item = request.result;
      if (item) {
        item.synced = true;
        store.put(item);
      }
    };

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function deleteFromStore(storeName: StoreType, id: string): Promise<void> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    store.delete(id);

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function getSyncQueue(): Promise<SyncQueueItem[]> {
  return getAllFromStore<SyncQueueItem>(STORES.SYNC_QUEUE);
}

export async function removeSyncQueueItem(id: string): Promise<void> {
  return deleteFromStore(STORES.SYNC_QUEUE, id);
}

export async function getPendingCount(): Promise<number> {
  const [attendance, tasks, assignmentUpdates] = await Promise.all([
    getAllFromStore<PendingAttendance>(STORES.PENDING_ATTENDANCE),
    getAllFromStore<PendingTask>(STORES.PENDING_TASKS),
    getAllFromStore<PendingAssignmentUpdate>(STORES.PENDING_ASSIGNMENT_UPDATES),
  ]);
  return (
    attendance.filter(a => !a.synced).length +
    tasks.filter(t => !t.synced).length +
    assignmentUpdates.filter(update => !update.synced).length
  );
}

// `organizationId` is used purely as an opaque string for key construction
// below (`${organizationId}:${scope}`) -- there's no validation that it's
// literally an organization id. A caller whose cache needs finer scoping
// than organization alone (Attendance.tsx passes a composite tenant-
// identity string including the platform-admin support session, so a
// session switch for the same org can't read/write another session's
// entry -- Codex P2, PR #6383 round 30) may pass any stable, unique string
// here instead, as long as it uses the SAME string consistently across its
// own get/set calls. Different callers already use disjoint `scope`
// values (see OFFLINE_READ_SCOPES), so this never collides across
// features even when their identity strings differ in shape.
export async function setOfflineCache<T>(
  scope: OfflineReadScope,
  organizationId: string,
  payload: T
): Promise<void> {
  const database = await openDatabase();
  const entry: OfflineCacheEntry<T> = {
    id: `${organizationId}:${scope}`,
    organization_id: organizationId,
    scope,
    payload,
    updated_at: new Date().toISOString(),
  };

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORES.OFFLINE_CACHE, 'readwrite');
    const store = transaction.objectStore(STORES.OFFLINE_CACHE);
    store.put(entry);

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

export async function getOfflineCache<T>(
  scope: OfflineReadScope,
  organizationId: string
): Promise<T | null> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORES.OFFLINE_CACHE, 'readonly');
    const store = transaction.objectStore(STORES.OFFLINE_CACHE);
    const request = store.get(`${organizationId}:${scope}`);

    request.onsuccess = () => {
      const entry = request.result as OfflineCacheEntry<T> | undefined;
      resolve(entry?.payload ?? null);
    };
    request.onerror = () => reject(request.error);
  });
}

// Clear all synced items (cleanup)
export async function clearSyncedItems(): Promise<void> {
  const database = await openDatabase();
  const stores = [STORES.PENDING_ATTENDANCE, STORES.PENDING_TASKS, STORES.PENDING_ASSIGNMENT_UPDATES];
  
  for (const storeName of stores) {
    const items = await getAllFromStore<{ id: string; synced?: boolean }>(storeName);
    const syncedIds = items.filter(i => i.synced).map(i => i.id);
    
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      
      syncedIds.forEach(id => store.delete(id));
      
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }
}
